class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedMap = new Map();

    this.searchContent = this.querySelector('.search__content');

    this.searchRecommendationEmpty =
      this.dataset.searchRecommendationEmpty === 'true';

    this.header = document.querySelector('header');

    this.toggleNavigationButton =
      document.querySelector('.toggle-navigation-button');

    this.isHeaderSearch =
      this.closest('header') !== null;

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };


    if (this.resetButton) {
      this.resetButton.addEventListener(
        'click',
        this.clear.bind(this)
      );
    }


    if (this.input) {
      this.input.addEventListener(
        'input',
        FoxTheme.utils.debounce(
          this.onChange.bind(this),
          300
        )
      );

      this.input.addEventListener(
        'focus',
        this.onFocus.bind(this)
      );
    }


    this.searchProductTypes?.addEventListener(
      'change',
      this.handleProductTypeChange.bind(this)
    );


    this.form?.addEventListener(
      'submit',
      this.onSubmit.bind(this)
    );


    this.boundHandleClickOutside =
      this.handleClickOutside.bind(this);

    document.addEventListener(
      'click',
      this.boundHandleClickOutside
    );


    document.addEventListener(
      'menu-drawer:open',
      () => {
        this.classList.remove(
          this.states.OPEN
        );

        document.body.classList.remove(
          this.states.SEARCH_OPEN
        );
      }
    );
  }


  /*
   * Search input
   */
  get input() {
    return this.querySelector(
      'input[type="search"]'
    );
  }


  /*
   * Reset button
   */
  get resetButton() {
    return this.querySelector(
      'button[type="reset"]'
    );
  }


  /*
   * Optional product type selector
   */
  get searchProductTypes() {
    return this.querySelector(
      '#SearchProductTypes'
    );
  }


  /*
   * Search form
   */
  get form() {
    return this.querySelector('form');
  }


  /*
   * Result container inside the header snippet
   */
  get resultsContainer() {
    return this.querySelector(
      '[id^="PredictiveSearchResults-"]'
    );
  }


  /*
   * Search input focus
   */
  onFocus() {
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );


    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(
          this.header.getBoundingClientRect().bottom
        )}px`
      );
    }


    if (
      this.isHeaderSearch &&
      !this.searchRecommendationEmpty
    ) {
      document.body.classList.add(
        this.states.SEARCH_OPEN
      );

      this.toggleNavButtonVisibility(false);
    }


    if (
      !this.searchRecommendationEmpty &&
      this.searchContent
    ) {
      this.searchContent.classList.remove(
        'hidden'
      );
    }


    this.classList.add(
      this.states.OPEN
    );


    const query = this.getQuery();

    if (query.length === 0) {
      if (
        this.searchRecommendationEmpty &&
        this.searchContent
      ) {
        this.searchContent.classList.add(
          'hidden'
        );
      }

      return;
    }


    const url =
      this.setupURL().toString();

    this.renderSection(url);
  }


  /*
   * Current search value
   */
  getQuery() {
    if (!this.input) {
      return '';
    }

    return this.input.value.trim();
  }


  /*
   * Clear search
   */
  clear(event = null) {
    event?.preventDefault();

    if (!this.input) {
      return;
    }


    this.input.value = '';

    this.cachedMap.clear();

    this.removeAttribute('results');
    this.removeAttribute('error');


    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }


    this.toggleSearchState(false);

    this.input.focus();
  }


  /*
   * Product type changed
   */
  handleProductTypeChange() {
    const query = this.getQuery();

    if (query.length === 0) {
      return;
    }


    this.cachedMap.clear();

    const url =
      this.setupURL().toString();

    this.renderSection(url);
  }


  /*
   * Normal search submit
   */
  onSubmit(event) {
    const productType =
      this.searchProductTypes?.value;

    const query =
      this.getQuery();


    if (
      !productType ||
      query.length === 0
    ) {
      return;
    }


    if (
      query.startsWith('product_type:')
    ) {
      return;
    }


    event.preventDefault();


    const params =
      new URLSearchParams();


    params.set(
      'q',
      `product_type:${productType} AND ${query}`
    );

    params.set(
      'options[prefix]',
      'last'
    );


    window.location.href =
      `${this.form.getAttribute('action')}?${params.toString()}`;
  }


  /*
   * Build Shopify Predictive Search URL
   *
   * IMPORTANT:
   * Product-only predictive search.
   */
  setupURL() {
    const url = new URL(
      `${window.shopUrl}${FoxTheme.routes.predictive_search_url}`
    );


    let searchTerm =
      this.getQuery();


    /*
     * Preserve existing product-type filtering.
     */
    if (
      this.searchProductTypes &&
      this.searchProductTypes.value !== ''
    ) {
      searchTerm =
        `product_type:${this.searchProductTypes.value} AND ${searchTerm}`;
    }


    url.searchParams.set(
      'q',
      searchTerm
    );


    
    url.searchParams.set(
      'resources[type]',
      'product'
    );


    url.searchParams.set(
      'resources[limit]',
      this.dataset.resultsLimit || 10
    );


    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );


    /*
     * Dedicated section that receives
     * Shopify's predictive_search object.
     */
    url.searchParams.set(
      'section_id',
      'predictive-search'
    );


    return url;
  }


  /*
   * Input changed
   */
  onChange() {
    const query =
      this.getQuery();


    if (query.length === 0) {
      this.clear();
      return;
    }


    const url =
      this.setupURL().toString();


    this.renderSection(url);
  }


  /*
   * Load results
   */
  renderSection(url) {
    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url);
      return;
    }


    this.renderSectionFromFetch(url);
  }


  /*
   * Cached result
   */
  renderSectionFromCache(url) {
    const responseText =
      this.cachedMap.get(url);


    this.renderSearchResults(
      responseText
    );


    this.setAttribute(
      'results',
      'true'
    );
  }


  /*
   * Fetch result from Shopify
   */
  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    this.removeAttribute('error');


    fetch(url)
      .then(async (response) => {
        const responseText =
          await response.text();


        if (!response.ok) {
          throw new Error(
            `Predictive search request failed with status ${response.status}`
          );
        }


        return responseText;
      })


      .then((responseText) => {
        this.renderSearchResults(
          responseText
        );


        this.cachedMap.set(
          url,
          responseText
        );
      })


      .catch((error) => {
        console.error(
          'Predictive search error:',
          error
        );


        this.setAttribute(
          'error',
          'Failed to load data'
        );


        if (this.resultsContainer) {
          this.resultsContainer.innerHTML = '';
        }
      })


      .finally(() => {
        this.setLoadingState(false);
      });
  }


  /*
   * Insert Shopify section response
   * into header predictive-search container.
   */
  renderSearchResults(responseText) {
    const targetElement =
      this.resultsContainer;


    if (!targetElement) {
      console.error(
        'Predictive search target container was not found.'
      );

      return;
    }


    const parser =
      new DOMParser();


    const parsedDocument =
      parser.parseFromString(
        responseText,
        'text/html'
      );


    /*
     * This element comes from:
     *
     * sections/predictive-search.liquid
     */
    const contentElement =
      parsedDocument.getElementById(
        'PredictiveSearchResults'
      );


    if (!contentElement) {
      console.error(
        'PredictiveSearchResults was not found in the Shopify response.',
        responseText
      );

      return;
    }


    targetElement.innerHTML =
      contentElement.innerHTML;


    if (this.searchContent) {
      this.searchContent.classList.remove(
        'hidden'
      );
    }


    if (
      this.isHeaderSearch &&
      !document.body.classList.contains(
        this.states.SEARCH_OPEN
      )
    ) {
      document.body.classList.add(
        this.states.SEARCH_OPEN
      );


      this.toggleNavButtonVisibility(
        true
      );
    }


    this.classList.add(
      this.states.OPEN
    );


    this.input?.setAttribute(
      'aria-expanded',
      'true'
    );


    this.setAttribute(
      'results',
      'true'
    );
  }


  /*
   * Close search when clicking outside
   */
  handleClickOutside(event) {
    const target =
      event.target;


    const shouldClose =
      this.isHeaderSearch
        ? !this.contains(target) &&
          (
            (
              target.classList.contains(
                'fixed-overlay'
              ) &&
              target.closest(
                '.header-section'
              )
            ) ||
            target.classList.contains(
              'header__search-close'
            )
          )
        : !this.contains(target);


    if (shouldClose) {
      setTimeout(() => {
        this.toggleSearchState(false);
      });
    }
  }


  /*
   * Open / close UI
   */
  toggleSearchState(isOpen) {
    this.classList.toggle(
      this.states.OPEN,
      isOpen
    );


    this.input?.setAttribute(
      'aria-expanded',
      isOpen ? 'true' : 'false'
    );


    if (this.isHeaderSearch) {
      document.body.classList.toggle(
        this.states.SEARCH_OPEN,
        isOpen
      );


      this.toggleNavButtonVisibility(
        !isOpen
      );
    }


    if (
      !isOpen &&
      this.searchRecommendationEmpty
    ) {
      this.searchContent?.classList.add(
        'hidden'
      );
    }
  }


  /*
   * Header navigation visibility
   */
  toggleNavButtonVisibility(isOpen) {
    if (!this.toggleNavigationButton) {
      return;
    }


    this.toggleNavigationButton.classList.toggle(
      'is-show',
      isOpen
    );
  }


  /*
   * Loading state
   */
  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute(
        'loading',
        'true'
      );


      this.resetButton?.classList.add(
        this.states.LOADING
      );


      return;
    }


    this.removeAttribute(
      'loading'
    );


    this.resetButton?.classList.remove(
      this.states.LOADING
    );
  }
}


/*
 * Prevent duplicate registration.
 */
if (
  !customElements.get(
    'predictive-search'
  )
) {
  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}